import { defineStore } from 'pinia'
import { DateTime } from 'luxon'
import { getEffectiveTimezone } from '~/utils/getEffectiveTimezone'
import type { SearchCoordinates } from '~/interfaces/SearchCoordinates'
import { computed, ref, shallowRef, watch } from 'vue'
import type { FavoritesIndexSuccess } from '~/services/api/generated'
import type { storeSearchContract } from '~/services/api/contracts/storeSearchContract'
import type { FavoriteStore } from '~/interfaces/favorites'
import { ALL_OPTION_VALUE } from '~/utils/allOptionValue'
import { Normalization } from '~/utils/normalization'
import { OrderType, GeocodingPlaceTypeValues, DeliveryContactType } from '~/enums'
import { isCustomerSelectableOrderType } from '~/utils/isCustomerSelectableOrderType'
import { resolveEventDateOnly } from '~/utils/resolveEventDateOnly'
import type { AddressSearchValue } from '~/lib/geo-providers/interfaces'
import { normalizeEventName } from '~/utils/normalizeEventName'
import { parseGuestCount } from '~/utils/parseGuestCount'
import type { GuestCountState } from '~/utils/GuestCountState'
import { isGroupDining } from '~/utils/isGroupDining'
import { readGuestCount } from '~/utils/readGuestCount'

type SearchQuery = string | { q?: string; address?: string; [key: string]: unknown } | null
interface EventDateTime {
  eventDate?: string | Date | null
  eventTime?: string | null
  [key: string]: unknown
}
interface EventDetails {
  guestCount?: number | string | null
  eventName?: string | null
  [key: string]: unknown
}
type SearchBounds = number[] | null
interface Pagination {
  page: number | null
  per_page: number | null
  total: number | null
  last_page: number | null
}
interface AddressDetails {
  address: string
  officeName: string
  suiteFloor: string
}
interface DeliveryInstructions {
  contactType: string
  contactPerson: string
  phone: string
  setupForMe: boolean
}
// Persisted search state only. Checkout PII (addressDetails,
// deliveryInstructions, orderComments) is deliberately NOT persisted here --
// see APP-524: those fields live in the store as in-memory refs and are never
// written to this JS-readable cookie.
interface SearchCookie {
  searchQuery: SearchQuery
  orderType: unknown
  eventDateTime: EventDateTime | null
  eventDetails: EventDetails | null
  coordinates: SearchCoordinates | null
  bbox: SearchBounds
  place_type: string | null
}
type FavoriteStoresApiResponse = Extract<FavoritesIndexSuccess, { success: boolean }>
interface SearchFilters {
  page?: number | null
  per_page?: number | null
  category_ids?: string | number
  dietaryOptions?: (string | number)[]
  tags?: (string | number)[]
  ratings?: (string | number)[]
  groupOrdering?: boolean
  individualPackaging?: boolean
  isNew?: boolean
  smallBusiness?: boolean
  reliabilitySuperstar?: boolean
  budgetPerPerson?: string | number | null
  distance?: string | number | null
  radius_miles?: string | number | null
  [key: string]: unknown
}
const hasSearchQueryValue = (value: SearchQuery): boolean => {
  return value !== null && value !== undefined && (typeof value !== 'string' || value.length > 0)
}
const hasStringValue = (value: string | null | undefined): boolean => {
  return value !== null && value !== undefined && value.length > 0
}
const hasNumberOrStringValue = (value: number | string | null | undefined): boolean => {
  if (typeof value === 'number') return value !== 0 && !Number.isNaN(value)
  return typeof value === 'string' && value.length > 0
}
const applyPaginationParams = (
  query: Record<string, unknown>,
  filters: { page?: number | null; per_page?: number | null }
): void => {
  if (filters.page !== null && filters.page !== undefined) {
    query['page'] = filters.page
  }
  if (filters.per_page !== null && filters.per_page !== undefined) {
    query['per_page'] = filters.per_page
  }
}
/**
 * @typedef {string | { q?: string, address?: string, [key: string]: unknown } | null} SearchQuery
 */

/**
 * @typedef {{ eventDate?: string | Date | null, eventTime?: string | null, [key: string]: unknown } | null} EventDateTime
 */

/**
 * @typedef {{ guestCount?: number | string | null, eventName?: string | null, [key: string]: unknown } | null} EventDetails
 */

/**
 * @typedef {{ lat: number | string, lng: number | string } | null} SearchCoordinates
 */

/**
 * @typedef {number[] | null} SearchBounds
 */

interface EventDetailsInput {
  eventName?: unknown
  guestCount?: unknown
}

const normalizeEventDetails = (input: EventDetailsInput): EventDetails => ({
  eventName: normalizeEventName(input.eventName),
  guestCount: parseGuestCount(input.guestCount)
})

export const useSearchStore = defineStore('search', () => {
  // Search state
  const restaurants = ref<ReturnType<typeof storeSearchContract.parse>['data']['stores']>([])
  const favoriteStores = ref<FavoriteStore[]>([])
  /** @type {import('vue').Ref<SearchQuery>} */
  const searchQuery = ref<SearchQuery>(null)
  const persistedOrderType = ref<OrderType>(OrderType.DINE_IN)
  /** @type {import('vue').Ref<EventDateTime>} */
  const eventDateTime = ref<EventDateTime | null>(null)
  const persistedEventDetails = ref<EventDetails | null>(null)
  // Event details cross a trust boundary (cookie, URL, forms): every write keeps a party size only
  // when it is a whole count of at least one, so a stored value is either valid or absent.
  const eventDetails = computed<EventDetails | null>({
    get: () => persistedEventDetails.value,
    set: (value) => {
      persistedEventDetails.value = value === null ? null : normalizeEventDetails(value)
    }
  })
  const guestCount = computed<GuestCountState>(() => readGuestCount(eventDetails.value?.guestCount))
  /** @type {import('vue').Ref<SearchCoordinates>} */
  const coordinates = ref<SearchCoordinates | null>(null)
  /** @type {import('vue').Ref<SearchBounds>} */
  const bbox = ref<SearchBounds>(null) // Bounding box: [minLng, minLat, maxLng, maxLat]
  /** @type {import('vue').Ref<string | null>} */
  const _placeType = ref<string | null>(null) // Place type (e.g., 'address', 'poi', 'place', 'postcode', 'region')
  const place_type = computed<string | null>({
    get: () => _placeType.value,
    set: (value) => {
      _placeType.value = Normalization.enumValue(value, GeocodingPlaceTypeValues, null)
    }
  })

  // Applies an address-search selection's common fields (query, coordinates, bounds, place
  // type) onto search state. Callers still own their own side effects (order type, navigation).
  const applyAddressSelection = (val: AddressSearchValue): void => {
    searchQuery.value = val.address
    coordinates.value = { lat: val.lat, lng: val.lng }
    bbox.value = val.bbox ?? null
    place_type.value = val.placeType ?? null
  }

  // Address text typed without a provider pick carries no coordinates, bounds, or place type; the
  // search API geocodes the text.
  const applyAddressText = (text: string): void => {
    searchQuery.value = text.trim()
    coordinates.value = null
    bbox.value = null
    place_type.value = null
  }

  // Checkout "Details" step inputs. In-memory only: these hold PII (delivery
  // address, contact name/phone) and are deliberately NOT persisted to the
  // mealz-search cookie (APP-524), so they survive in-app navigation within a
  // session but reset on a full page reload. Cleared after a successful order.
  const addressDetails = ref<AddressDetails>({ address: '', officeName: '', suiteFloor: '' })
  const deliveryInstructions = ref<DeliveryInstructions>({
    contactType: DeliveryContactType.ME,
    contactPerson: '',
    phone: '',
    setupForMe: false
  })
  const orderComments = ref<string>('')
  const pagination = ref<Pagination>({
    page: null,
    per_page: null,
    total: null,
    last_page: null
  })
  const loading = ref(false)
  const errorResolver = shallowRef<(() => string) | null>(null)
  const error = computed(() => errorResolver.value?.() ?? null)
  const setSearchError = (message: () => string): void => {
    errorResolver.value = message
  }
  const clearSearchError = (): void => {
    errorResolver.value = null
  }
  const favoritesLoading = ref(false)
  const favoritesErrorResolver = shallowRef<(() => string) | null>(null)
  const favoritesError = computed(() => favoritesErrorResolver.value?.() ?? null)
  const setFavoritesError = (message: () => string): void => {
    favoritesErrorResolver.value = message
  }
  const clearFavoritesError = (): void => {
    favoritesErrorResolver.value = null
  }
  const hasInitialized = ref(false)
  const favoritesHasInitialized = ref(false)
  const eventSlug = ref<string | null>(null) // For event-based searches

  const isSyncingFavorites = ref(false)

  // Delivery is not offered to customers: a delivery value from a form, the URL, or a persisted
  // cookie leaves the order type unchanged, so stale state falls back to Group Dining.
  const setOrderType = (value: unknown): boolean => {
    if (!isCustomerSelectableOrderType(value)) return false

    persistedOrderType.value = value
    return true
  }

  const orderType = computed({
    get: (): OrderType => persistedOrderType.value,
    set: setOrderType
  })

  // Group Dining bookings need a party size. Search never does: it filters by party size only when one is given.
  const needsGuestCount = computed(
    () => isGroupDining(orderType.value) && !guestCount.value.provided
  )

  // A party size from a shared link wins over the stored one, but only when it is a valid count.
  const restoreGuestCountFromUrl = (raw: unknown): void => {
    const count = readGuestCount(raw)
    if (count.provided) {
      eventDetails.value = { ...eventDetails.value, guestCount: count.value }
    }
  }

  const getCurrentDateForQuery = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const resolveEventDateForQuery = (
    value: string | Date | null | undefined,
    fallbackToToday = false,
    timezone?: string | null
  ): string => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const dateTime = DateTime.fromJSDate(value, { zone: getEffectiveTimezone(timezone) })
      if (!dateTime.isValid) return ''
      return dateTime.toISODate() ?? ''
    }

    const raw = String(value ?? '').trim()
    if (raw) return raw

    return fallbackToToday ? getCurrentDateForQuery() : ''
  }

  // Cookie persistence for search state
  const searchCookie = useCookie<SearchCookie>('mealz-search', {
    default: () => ({
      searchQuery: null,
      orderType: null,
      eventDateTime: null,
      eventDetails: null,
      coordinates: null,
      bbox: null,
      place_type: null
    }),
    // Cookie options
    maxAge: 60 * 60 * 24 * 7, // 7 days
    path: '/',
    sameSite: 'lax',
    // secure: true (HTTPS-only transport). HttpOnly is intentionally NOT set:
    // this cookie holds only non-sensitive search state (query, order type,
    // coordinates, bbox, event fields) that must stay JS-readable for the
    // reactive useCookie read/write pattern this store depends on. Sensitive
    // checkout PII was removed from this cookie under APP-524 so there is
    // nothing here that HttpOnly needs to protect.
    //
    // Secure in production (served over HTTPS) but not in local dev: the dev
    // origin (app.mealz.test) is plain HTTP and not `localhost`, so a Secure
    // cookie would be silently dropped and search state would stop persisting
    // across reloads locally. Same production-only gating as the HTTPS headers.
    secure: !import.meta.dev
  })

  // Restore search state from cookie. The cookie is a trust boundary: a payload
  // persisted by an older release may no longer match the current shape, so every
  // field is normalized before it is trusted.
  const restoreSearchFromCookie = () => {
    const saved = searchCookie.value

    if (hasSearchQueryValue(saved.searchQuery)) {
      searchQuery.value =
        typeof saved.searchQuery === 'string'
          ? Normalization.trimmedStringValue(saved.searchQuery, saved.searchQuery)
          : saved.searchQuery
    }

    setOrderType(saved.orderType)

    if (saved.eventDateTime !== null && saved.eventDateTime !== undefined) {
      eventDateTime.value = {
        ...saved.eventDateTime,
        eventTime: Normalization.trimmedStringValue(saved.eventDateTime.eventTime, null)
      }
    }

    if (saved.eventDetails !== null && saved.eventDetails !== undefined) {
      eventDetails.value = saved.eventDetails
    }

    if (
      saved.coordinates !== null &&
      saved.coordinates !== undefined &&
      Normalization.floatValue(saved.coordinates.lat, null) !== null &&
      Normalization.floatValue(saved.coordinates.lng, null) !== null
    ) {
      coordinates.value = saved.coordinates
    }

    if (Array.isArray(saved.bbox) && saved.bbox.length === 4) {
      const parsedBbox = saved.bbox.map((coord) => Normalization.floatValue(coord, null))
      if (parsedBbox.every((coord): coord is number => coord !== null)) {
        bbox.value = parsedBbox
      }
    }

    const normalizedPlaceType = Normalization.enumValue(
      saved.place_type,
      GeocodingPlaceTypeValues,
      null
    )
    if (normalizedPlaceType !== null) {
      place_type.value = normalizedPlaceType
    }

    // Note: addressDetails, deliveryInstructions, and orderComments are NOT
    // restored from the cookie -- they are no longer persisted there (APP-524).
    // They remain plain in-memory refs, populated by the checkout form.
  }

  const handleFavoritesApiResponse = (response: FavoriteStoresApiResponse): void => {
    favoriteStores.value = response.data.items
  }

  // Save search state to cookie
  const saveSearchToCookie = () => {
    searchCookie.value = {
      searchQuery: searchQuery.value,
      orderType: orderType.value,
      eventDateTime: eventDateTime.value,
      eventDetails: eventDetails.value,
      coordinates: coordinates.value,
      bbox: bbox.value,
      place_type: place_type.value
    }
  }

  // Auto-save search state whenever it changes. Checkout PII
  // (addressDetails, deliveryInstructions, orderComments) is intentionally
  // excluded -- it is not persisted to the cookie (APP-524).
  watch(
    [searchQuery, orderType, eventDateTime, eventDetails, coordinates, bbox, place_type],
    () => {
      saveSearchToCookie()
    },
    { deep: true }
  )

  // Clear checkout-only fields after a successful order submission.
  // Leaves search-related fields (searchQuery, orderType, coordinates, eventDateTime,
  // eventDetails, etc.) intact so the user's general preferences persist across orders.
  const clearCheckoutFields = () => {
    addressDetails.value = { address: '', officeName: '', suiteFloor: '' }
    deliveryInstructions.value = {
      contactType: DeliveryContactType.ME,
      contactPerson: '',
      phone: '',
      setupForMe: false
    }
    orderComments.value = ''
  }

  // Restore search state on store initialization
  restoreSearchFromCookie()

  // Build query parameters (shared logic) - returns an object for $fetch query
  const buildQueryParams = (filters: SearchFilters = {}): Record<string, unknown> => {
    const query: Record<string, unknown> = {}

    // Pagination
    applyPaginationParams(query, filters)

    // Add filters from filters store
    const hasNumericCuisineOption =
      hasNumberOrStringValue(filters.category_ids ?? null) &&
      filters.category_ids !== ALL_OPTION_VALUE
    query['cuisineOption'] = hasNumericCuisineOption
      ? Normalization.floatValue(filters.category_ids, Number.NaN)
      : ALL_OPTION_VALUE

    if (filters.dietaryOptions && filters.dietaryOptions.length > 0) {
      query['dietaryOptions'] = filters.dietaryOptions.join(',')
    }

    if (filters.tags && filters.tags.length > 0) {
      query['tags'] = filters.tags.join(',')
    }

    // ratings is single-select in the UI (selectedRatings.value = [rating]); the contract wants one number.
    const rating = Number(filters.ratings?.[0])
    query['ratings'] = Number.isFinite(rating) ? rating : ALL_OPTION_VALUE

    if (filters.groupOrdering === true) {
      query['groupOrdering'] = true
    }

    if (filters.individualPackaging === true) {
      query['individualPackaging'] = true
    }

    if (filters.isNew === true) {
      query['isNew'] = true
    }

    if (filters.smallBusiness === true) {
      query['smallBusiness'] = true
    }

    if (filters.reliabilitySuperstar === true) {
      query['reliabilitySuperstar'] = true
    }

    // budgetPerPerson has no backend param; MoreFiltersModal.vue's Budget per person field is inert until the API supports it.

    if (filters.distance !== null && filters.distance !== undefined) {
      query['distance'] = Normalization.floatValue(filters.distance, Number.NaN)
    }

    // Add order type to search parameters
    query['order_type'] = orderType.value

    // requested_date is a calendar date; requested_time carries the time of day.
    if (eventDateTime.value) {
      const requestedDate = resolveEventDateForQuery(
        eventDateTime.value.eventDate,
        Boolean(eventDateTime.value.eventTime)
      )
      const requestedDateOnly = requestedDate ? resolveEventDateOnly(requestedDate) : null
      if (requestedDateOnly !== null) {
        query['requested_date'] = requestedDateOnly
      }
      if (hasStringValue(eventDateTime.value.eventTime)) {
        query['requested_time'] = eventDateTime.value.eventTime
      }
    }

    // Only Group Dining narrows the search by party size; Pickup never sends one. event_name has no search param.
    if (isGroupDining(orderType.value) && guestCount.value.provided) {
      query['guest_count'] = guestCount.value.value
    }

    // Add event slug if this is an event-based search
    if (hasStringValue(eventSlug.value)) {
      query['event_slug'] = eventSlug.value
    }

    return query
  }

  const buildFavoritesQueryParams = (
    filters: Pick<SearchFilters, 'page' | 'per_page'> = {}
  ): Record<string, unknown> => {
    const query: Record<string, unknown> = {}

    applyPaginationParams(query, filters)

    return query
  }

  const getAddressString = (): string => {
    const currentSearchQuery = searchQuery.value
    if (currentSearchQuery === null) return ''
    if (typeof currentSearchQuery === 'string') {
      return currentSearchQuery
    }
    if (hasStringValue(currentSearchQuery.q)) {
      return currentSearchQuery.q ?? ''
    }
    if (hasStringValue(currentSearchQuery.address)) {
      return currentSearchQuery.address ?? ''
    }
    return ''
  }

  const queriesAreEqual = (incoming: SearchQuery, current: SearchQuery): boolean => {
    return Normalization.deepEqual(incoming, current)
  }

  const updateSearchQueryFromResponse = (incomingQuery: SearchQuery | undefined): void => {
    if (incomingQuery === undefined || incomingQuery === null) {
      return
    }
    if (!queriesAreEqual(incomingQuery, searchQuery.value)) {
      searchQuery.value = incomingQuery
    }
  }

  // Apply the validated store-search response.
  const handleApiResponse = (response: ReturnType<typeof storeSearchContract.parse>): void => {
    pagination.value = {
      page: response.data.pagination.current_page,
      per_page: response.data.pagination.per_page,
      total: response.data.pagination.total,
      last_page: response.data.pagination.last_page
    }
    restaurants.value = response.data.stores
    updateSearchQueryFromResponse(response.data.query)
  }

  // Update favorite state for a store in the local list
  const updateStoreFavorite = (storeId: string | number, is_favorite: boolean): void => {
    restaurants.value = restaurants.value.map((restaurant) =>
      restaurant.id === storeId ? { ...restaurant, is_favorite } : restaurant
    )
    favoriteStores.value = favoriteStores.value.map((favoriteStore) =>
      favoriteStore.id === storeId ? { ...favoriteStore, is_favorite } : favoriteStore
    )
  }

  // Apply the current bounding box and place type onto an outgoing search query
  const applyBoundsAndPlaceTypeParams = (query: Record<string, unknown>): void => {
    if (bbox.value && Array.isArray(bbox.value) && bbox.value.length === 4) {
      query['bbox'] = bbox.value.join(',')
    }

    if (place_type.value !== null && place_type.value !== undefined && place_type.value !== '') {
      query['place_type'] = place_type.value
    }
  }

  // Reset search state
  const resetSearch = () => {
    restaurants.value = []
    searchQuery.value = null
    coordinates.value = null
    bbox.value = null
    place_type.value = null
    pagination.value = { page: null, per_page: null, total: null, last_page: null }
    loading.value = false
    clearSearchError()
    hasInitialized.value = false
  }

  // Clears favourite stores, the rendered per-result favourite flag and checkout PII on
  // identity loss; general search preferences (query, coordinates, order type) stay.
  const clearAccountScopedState = () => {
    favoriteStores.value = []
    restaurants.value = restaurants.value.map((restaurant) => ({
      ...restaurant,
      is_favorite: false
    }))
    clearCheckoutFields()
    // Owns the spinners too: a superseded fetch leaves its own `finally` write unmade.
    loading.value = false
    favoritesLoading.value = false
  }

  const setEventDetails = (input: EventDetailsInput): void => {
    eventDetails.value = normalizeEventDetails(input)
  }

  return {
    // State
    restaurants,
    favoriteStores,
    searchQuery,
    orderType,
    eventDateTime,
    eventDetails,
    guestCount,
    needsGuestCount,
    coordinates,
    bbox,
    place_type,
    addressDetails,
    deliveryInstructions,
    orderComments,
    pagination,
    loading,
    error,
    favoritesLoading,
    favoritesError,
    hasInitialized,
    favoritesHasInitialized,
    eventSlug,
    isSyncingFavorites,

    // Methods
    setSearchError,
    clearSearchError,
    setFavoritesError,
    clearFavoritesError,
    buildQueryParams,
    buildFavoritesQueryParams,
    handleApiResponse,
    handleFavoritesApiResponse,
    applyBoundsAndPlaceTypeParams,
    resolveEventDateForQuery,
    resetSearch,
    clearCheckoutFields,
    clearAccountScopedState,
    restoreSearchFromCookie,
    getAddressString,
    updateStoreFavorite,
    setOrderType,
    restoreGuestCountFromUrl,
    applyAddressSelection,
    applyAddressText,
    setEventDetails
  }
})
